/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import {getActiveBranchId} from '../utils/branch';
import React,{useState,useEffect}from'react';
import{useNavigate}from'react-router-dom';
import{useMultiMaster}from'../hooks/useMaster';
import Layout from'../components/layout/Layout';
import SlidePanel from'../components/common/SlidePanel';
import Badge from'../components/common/Badge';
import{Input,Select}from'../components/common/FormField';
import api from'../services/api';
import{openPrintWindow}from'../utils/printUtils';
import TerminologySearch from'../components/common/TerminologySearch';
import{fmtDateTime}from'../utils/helpers';
import DrugSearchInput from'../components/common/DrugSearchInput';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import {autoTranslateBatch} from '../utils/autoTranslate';
import { shaGetStatusForVisit } from '../services/sha.api';
import { isShaCoveredPatient } from '../utils/shaPatient';
import ShrPanel from '../components/common/ShrPanel';

const DRUG_FREQ=['Once daily','Twice daily','Thrice daily','Four times daily','Every 6 hours','Every 8 hours','As needed (SOS)','Bedtime'];

// Consistent input styles
const inputCls="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all bg-white placeholder:text-slate-400";
const textareaCls="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all bg-white placeholder:text-slate-400 resize-none";
const selectCls="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all bg-white";
const DRUG_ROUTE=['Oral','IV','IM','SC','Topical','Inhaled','Sublingual','Nasal'];

// safe doctor name — field already contains "Dr." sometimes
const docName=(fn:string,ln:string)=>{
  const n=`${fn||''} ${ln||''}`.trim();
  return n?( n.startsWith('Dr.')?n:`Dr. ${n}`):'—';
};

// stat card mini
const Mini=({label,value,icon,color}:any)=>{
  const bg:any={blue:'from-blue-500 to-[#02357A]',green:'from-emerald-500 to-teal-600',amber:'from-amber-500 to-orange-500',purple:'from-purple-500 to-indigo-600'};
  return(
    <div className={`relative overflow-hidden rounded-2xl p-4 text-white bg-gradient-to-br ${bg[color]||bg.blue} shadow-lg`}>
      <p className="text-xs font-semibold opacity-80 uppercase tracking-wider">{label}</p>
      <p className="text-3xl font-black mt-1">{value}</p>
      <span className="absolute right-3 top-3 text-3xl opacity-20">{icon}</span>
    </div>
  );
};

// tab button
const Tab=({active,onClick,children}:any)=>(
  <button onClick={onClick} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${active?'bg-[#02357A] text-white shadow-lg shadow-[#02357A]/20':'bg-white text-slate-600 border border-slate-200 hover:border-[#02357A] hover:text-[#02357A]'}`}>
    {children}
  </button>
);

// section card
const Card=({title,subtitle,children,action}:any)=>(
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    {(title||action)&&(
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div>{title&&<h3 className="font-bold text-slate-800">{title}</h3>}{subtitle&&<p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}</div>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </div>
);

// field label
const Lbl=({children}:any)=><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{children}</label>;

export default function OPDPage(){
  const {t,i18n}=useTranslation();
  const navigate=useNavigate();
  const{masters:opd_masters}=useMultiMaster(['DRUG_ROUTE']);
  const MASTER_ROUTES=opd_masters['DRUG_ROUTE']?.map((m:any)=>m.value)||DRUG_ROUTE;
  const[tab,setTab]=useState<'queue'|'vitals'|'consult'|'proc'|'rx'>('queue');
  const[selected,setSelected]=useState<any>(null);
  const[queue,setQueue]=useState<any[]>([]);
  const[stats,setStats]=useState<any>({});
  const[vitals,setVitals]=useState<any>({temperature:'',systolicBp:'',diastolicBp:'',heartRate:'',respiratoryRate:'',oxygenSaturation:'',weight:'',height:'',bloodSugar:'',painScore:''});
  const[consult,setConsult]=useState({chiefComplaint:'',historyOfPresentIllness:'',physicalExamination:'',provisionalDiagnosis:'',finalDiagnosis:'',icdCode:'',treatmentPlan:'',advice:'',followUpRequired:false,followUpDate:'',followUpReason:'',pastMedicalHistory:'',familyHistory:'',socialHistory:'',medicationHistory:'',systemicExamination:'',referredTo:''});
  const[procCatalog,setProcCatalog]=useState<any[]>([]);
  const[patientProcedures,setPatientProcedures]=useState<any[]>([]);
  const[procForm,setProcForm]=useState({procedureId:'',procedureName:'',cost:'',notes:''});
  const[rxItems,setRxItems]=useState<any[]>([{drugName:'',dosage:'',frequency:'Once daily',route:'Oral',duration:'5 days',instructions:'After food'}]);
  const[lastPrescription,setLastPrescription]=useState<any>(null);
  const[saving,setSaving]=useState(false);
  const[msg,setMsg]=useState<{text:string,ok:boolean}|null>(null);
  const[showVitalsHistory,setShowVitalsHistory]=useState(false);
  const[vitalsHistory,setVitalsHistory]=useState<any[]>([]);
  const[consultationHistory,setConsultationHistory]=useState<any[]>([]);
  const[prescriptionHistory,setPrescriptionHistory]=useState<any[]>([]);
  const[showConsultHistory,setShowConsultHistory]=useState(false);
  const[showRxHistory,setShowRxHistory]=useState(false);
  const[shaStatus,setShaStatus]=useState<any>(null);
  const[lastConsultId,setLastConsultId]=useState<string>('');

  const notify=(text:string,ok=true)=>{setMsg({text,ok});setTimeout(()=>setMsg(null),3500);};

  useEffect(()=>{
    api.getTodayQueue().then(r=>{
      const list=r?.data?.appointments||r?.data?.queue||(Array.isArray(r?.data)?r.data:[]);
      setQueue(list.filter(Boolean));
    }).catch(()=>{});
    api.getOPDStats().then(r=>setStats(r?.data||{})).catch(()=>{});
    api.getProcedureCatalog().then(r=>setProcCatalog(r?.data||[])).catch(()=>{});
  },[]);

  const selectPatient=(apt:any)=>{
    const pid=apt.patient_id||apt.patientId;
    setSelected(apt);setTab('queue');setLastConsultId('');setShaStatus(null);
    loadPatientProcedures(pid);
    loadConsultHistory(pid);
    loadPrescriptionHistory(pid);
    const visitId=apt.id;
    if(visitId) shaGetStatusForVisit(visitId).then(r=>setShaStatus(r?.data||null)).catch(()=>{});
  };

  const loadConsultHistory=async(patientId:string)=>{
    try{
      const r=await api.getConsultations({patientId,take:10});
      const list=r?.data?.data||r?.data?.consultations||(Array.isArray(r?.data)?r.data:[]);
      setConsultationHistory(list.filter(Boolean));
    }catch{}
  };

  const loadPrescriptionHistory=async(patientId:string)=>{
    try{
      const r=await api.getPrescriptions?.(patientId);
      const list=r?.data?.prescriptions||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
      setPrescriptionHistory(list.filter(Boolean));
    }catch{}
  };

  const loadVitalsHistory=async(patientId:string)=>{
    const r=await api.getOPDVitals?.(patientId)||await api.getVitals(patientId);
    const list=r?.data?.vitals||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
    setVitalsHistory(list);setShowVitalsHistory(true);
  };

  const loadPatientProcedures=async(patientId:string)=>{
    try{const r=await api.getPatientProcedures(patientId);setPatientProcedures(r?.data||[]);}catch{}
  };

  const saveVitals=async()=>{
    if(!selected)return notify('Select a patient first',false);
    setSaving(true);
    try{
      const h=vitals.height?+vitals.height/100:0;
      const bmi=vitals.weight&&h?(+vitals.weight/(h*h)).toFixed(1):'';
      await api.recordVitals({...vitals,patientId:selected.patient_id||selected.patientId,bmi});
      notify('✅ Vitals saved!');setTab('consult');
      loadVitalsHistory(selected.patient_id||selected.patientId);
    }catch(e:any){notify(e?.response?.data?.message||'Error saving vitals',false);}
    setSaving(false);
  };

  const saveConsultation=async()=>{
    if(!selected)return notify('Select a patient first',false);
    setSaving(true);
    try{
      const c=await api.createConsultation({...consult,patientId:selected.patient_id||selected.patientId,doctorId:selected.doctor_id||selected.doctorId,appointmentId:selected.id,branchId:getActiveBranchId()||undefined});
      const consultId=c?.data?.id;
      if(consultId){
        setLastConsultId(consultId);
        shaGetStatusForVisit(consultId).then(r=>setShaStatus(r?.data||null)).catch(()=>{});
      }
      if(consult.followUpRequired&&consult.followUpDate){
        await api.createFollowup({patientId:selected.patient_id||selected.patientId,consultationId:c?.data?.id,doctorId:selected.doctor_id||selected.doctorId,followUpDate:consult.followUpDate,reason:consult.followUpReason||consult.advice}).catch(()=>{});
      }
      notify('✅ Consultation saved!');setTab('rx');
      loadConsultHistory(selected.patient_id||selected.patientId);
    }catch(e:any){notify(e?.response?.data?.message||'Error',false);}
    setSaving(false);
  };

  const savePrescription=async()=>{
    if(!selected)return notify('Select a patient first',false);
    const drugs=rxItems.filter(i=>i.drugName);
    if(!drugs.length)return notify('Add at least one drug',false);
    setSaving(true);
    try{
      const r=await api.createPrescription({patientId:selected.patient_id||selected.patientId,doctorId:selected.doctor_id||selected.doctorId,prescriptionDate:new Date().toISOString().split('T')[0],items:drugs});
      setLastPrescription(r?.data);
      loadPrescriptionHistory(selected.patient_id||selected.patientId);
      await api.updateAppointmentStatus(selected.id,'COMPLETED').catch(()=>{});
      setQueue(q=>q.map((x:any)=>x.id===selected.id?{...x,status:'COMPLETED'}:x));
      notify('✅ Prescription saved!');
    }catch(e:any){notify(e?.response?.data?.message||'Error',false);}
    setSaving(false);
  };

  const logProcedure=async(quick?:any)=>{
    if(!selected)return notify('Select a patient first',false);
    const payload=quick?{procedureId:quick.id,procedureName:quick.procedureName,cost:quick.cost}:procForm;
    if(!payload.procedureName)return notify('Enter a procedure name',false);
    setSaving(true);
    try{
      await api.logProcedure({...payload,patientId:selected.patient_id||selected.patientId,appointmentId:selected.id,doctorId:selected.doctor_id||selected.doctorId});
      notify('✅ Procedure logged!');setProcForm({procedureId:'',procedureName:'',cost:'',notes:''});
      loadPatientProcedures(selected.patient_id||selected.patientId);
    }catch(e:any){notify(e?.response?.data?.message||'Error',false);}
    setSaving(false);
  };

  const closeVisitAndPrint=async()=>{
    if(!selected)return;
    setSaving(true);
    try{
      await api.updateAppointment(selected.id,{visitClosed:true,status:'COMPLETED'}).catch(()=>{});
      const dn=docName(selected.doctor_first_name,selected.doctor_last_name);
      const pn=`${selected.patient_first_name||''} ${selected.patient_last_name||''}`.trim();
      // Same auto-translate-on-print treatment used for Discharge Summary / MAR printing: the
      // clinician's free-text consultation fields get machine-translated (cached
      // LibreTranslate) into the current UI language, falling back to the original text if the
      // UI is English or translation fails.
      const lang=(i18n.language||'en').split('-')[0];
      const NOTE_FIELDS=[consult.chiefComplaint,consult.finalDiagnosis,consult.treatmentPlan,consult.advice].filter(Boolean);
      const tr=lang==='en'?{}:await autoTranslateBatch(NOTE_FIELDS,lang);
      const tx=(v:string|undefined)=>v?(tr[v]||v):v;
      await openPrintWindow(`
        <div class="section">
          <div class="row">
            <div class="field"><div class="lbl">${t('common.patient','Patient')}</div><div class="val">${pn} (${selected.uhid||''})</div></div>
            <div class="field"><div class="lbl">${t('common.doctor','Doctor')}</div><div class="val">${dn}</div></div>
          </div>
          <div class="row">
            <div class="field"><div class="lbl">${t('common.date','Date')}</div><div class="val">${new Date().toLocaleDateString('en-KE')}</div></div>
            <div class="field"><div class="lbl">${t('opd.token','Token')}</div><div class="val">${selected.token_number||selected.tokenNumber||'—'}</div></div>
          </div>
        </div>
        ${consult.chiefComplaint?`<h3 class="section-title">${t('consultation.chiefComplaint','Chief Complaint').replace(' *','')}</h3><p class="data-val">${tx(consult.chiefComplaint)}</p>`:''}
        ${consult.finalDiagnosis?`<h3 class="section-title">${t('discharge.finalDiagnosis','Final Diagnosis')}</h3><p class="data-val"><strong>${tx(consult.finalDiagnosis)}</strong>${consult.icdCode?` (${consult.icdCode})`:''}</p>`:''}
        ${consult.treatmentPlan?`<h3 class="section-title">${t('consultation.treatmentPlan','Treatment Plan')}</h3><p class="data-val">${tx(consult.treatmentPlan)}</p>`:''}
        ${consult.advice?`<h3 class="section-title">${t('opd.advice','Advice')}</h3><p class="data-val">${tx(consult.advice)}</p>`:''}
        ${consult.followUpRequired&&consult.followUpDate?`<h3 class="section-title">${t('consultation.followUpLabel','Follow-up')}</h3><p class="data-val">${t('opd.returnOn','Return on:')} <strong>${consult.followUpDate}</strong></p>`:''}
      `,t('opd.visitSummaryTitle','OPD Visit Summary'));
      setQueue(q=>q.filter((a:any)=>a.id!==selected.id));
      setSelected(null);notify('✅ Visit closed!');
    }catch{notify('Error closing visit',false);}
    setSaving(false);
  };

  const printPrescription=async()=>{
    if(!selected)return;
    const items=lastPrescription?.items||rxItems.filter((i:any)=>i.drugName);
    const rxNo=lastPrescription?.prescriptionNumber||`RX-${Date.now()}`;
    const dn=docName(selected.doctor_first_name,selected.doctor_last_name);
    const pn=`${selected.patient_first_name||''} ${selected.patient_last_name||''}`.trim();
    let qr='';
    try{const Q=await import('qrcode');qr=await Q.toDataURL(JSON.stringify({rx:rxNo,patient:pn}),{width:100,margin:1});}catch{}
    // Same auto-translate-on-print treatment used elsewhere for drug names: machine-translated
    // (cached LibreTranslate) into the current UI language, falling back to the original name
    // if the UI is English or translation fails.
    const lang=(i18n.language||'en').split('-')[0];
    const drugNames=items.map((i:any)=>i.drugName).filter(Boolean);
    const tr=lang==='en'?{}:await autoTranslateBatch(drugNames,lang);
    const tx=(v:string|undefined)=>v?(tr[v]||v):v;
    await openPrintWindow(`
      <div class="section">
        <div class="row">
          <div class="field"><div class="lbl">${t('common.patient','Patient')}</div><div class="val">${pn} (${selected.uhid||''})</div></div>
          <div class="field"><div class="lbl">${t('common.doctor','Doctor')}</div><div class="val">${dn} — ${selected.specialization||''}</div></div>
        </div>
        <div class="row">
          <div class="field"><div class="lbl">${t('opd.rxNo','Rx No')}</div><div class="val">${rxNo}</div></div>
          <div class="field"><div class="lbl">${t('common.date','Date')}</div><div class="val">${new Date().toLocaleDateString('en-KE')}</div></div>
        </div>
      </div>
      <table class="data-table"><thead><tr><th>${t('discharge.drug','Drug')}</th><th>${t('opd.strength','Strength')}</th><th>${t('discharge.frequency','Frequency')}</th><th>${t('opd.route','Route')}</th><th>${t('discharge.duration','Duration')}</th><th>${t('discharge.instructions','Instructions')}</th></tr></thead>
        <tbody>${items.map((i:any)=>`<tr><td>${tx(i.drugName)||''}</td><td>${i.dosage||''}</td><td>${i.frequency||''}</td><td>${i.route||''}</td><td>${i.duration||''}</td><td>${i.instructions||''}</td></tr>`).join('')}</tbody>
      </table>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:40px;">
        ${qr?`<div style="text-align:center"><img src="${qr}" width="80"/><p style="font-size:9px;color:#94a3b8">${t('opd.scanToVerify','Scan to verify')}</p></div>`:'<div></div>'}
        <div style="text-align:center"><div style="border-top:1px solid #334155;width:180px;padding-top:6px;font-size:11px;color:#334155">${dn}</div><div style="font-size:10px;color:#94a3b8">${selected.specialization||''}</div></div>
      </div>
    `,t('opd.prescriptionTitle','Prescription'),t('opd.computerGeneratedRxNote','Computer-generated prescription. Valid only with doctor seal.'));
  };

  const bmi=vitals.weight&&vitals.height?(+vitals.weight/((+vitals.height/100)**2)).toFixed(1):'—';

  const QUEUE_BORDER:any={SCHEDULED:'border-l-blue-500',CHECKED_IN:'border-l-emerald-500',IN_CONSULTATION:'border-l-amber-500',COMPLETED:'border-l-slate-300',CANCELLED:'border-l-red-400'};
  const CALLING_STYLE:any={WAITING:'bg-slate-100 text-slate-500',CALLED:'bg-amber-100 text-amber-700',IN_CONSULTATION:'bg-green-100 text-green-700',SKIPPED:'bg-red-100 text-red-600',DONE:'bg-slate-100 text-slate-400'};
  const CALLING_LABEL:any={WAITING:'⏳ Waiting',CALLED:'📢 Called',IN_CONSULTATION:'🩺 In Consultation',SKIPPED:'⏭️ Skipped',DONE:'✓ Done'};

  const TABS=[['queue','🏥 Queue'],['vitals','❤️ Vitals'],['consult','📋 Consultation'],['proc','🩹 Procedures'],['rx','💊 Prescription']] as const;

  return(
    <Layout title="OPD — Outpatient Management">
      {/* Notification toast */}
      {msg&&(
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border animate-slide-in
          ${msg.ok?'bg-green-50 border-green-200 text-green-800':'bg-red-50 border-red-200 text-red-800'}`}>
          {msg.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Mini label="Today's OPD" value={stats.todayConsultations||queue.length||0} icon="🏥" color="blue"/>
        <Mini label="In Queue" value={queue.filter((q:any)=>q?.status==='SCHEDULED').length} icon="⏳" color="amber"/>
        <Mini label="In Progress" value={queue.filter((q:any)=>q?.status==='IN_CONSULTATION'||q?.callingStatus==='IN_CONSULTATION').length} icon="🩺" color="green"/>
        <Mini label="Completed" value={queue.filter((q:any)=>q?.status==='COMPLETED').length} icon="✅" color="purple"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-5">

        {/* LEFT — Queue panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Queue header */}
            <div className="px-4 py-3 bg-gradient-to-r from-[#02357A] to-[#015CA6] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">Today's Queue</h3>
                <p className="text-blue-200 text-xs mt-0.5">{queue.length} patients</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-black text-sm">{queue.filter((q:any)=>q?.callingStatus!=='DONE'&&q?.status!=='COMPLETED').length}</span>
              </div>
            </div>

            {/* Queue list */}
            <div className="divide-y divide-slate-100 max-h-[calc(100vh-280px)] overflow-y-auto">
              {queue.length===0?(
                <div className="py-12 text-center text-slate-400">
                  <p className="text-3xl mb-2">📋</p>
                  <p className="text-sm">No patients today</p>
                </div>
              ):queue.map((apt:any)=>apt&&(
                <div key={apt.id}
                  onClick={()=>selectPatient(apt)}
                  className={`p-3.5 cursor-pointer border-l-4 transition-all
                    ${QUEUE_BORDER[apt.status]||'border-l-slate-300'}
                    ${selected?.id===apt.id?'bg-blue-50':'hover:bg-slate-50'}`}>

                  {/* Token + Status */}
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-2xl font-black text-[#02357A]">{apt.token_number||apt.tokenNumber||'—'}</span>
                    <Badge label={apt.status||'—'} status={apt.status}/>
                  </div>

                  {/* Patient name */}
                  <p className="font-semibold text-slate-800 text-sm">
                    {`${apt.patient_first_name||''} ${apt.patient_last_name||''}`.trim()||'—'}
                  </p>
                  <p className="text-xs text-[#02357A] font-mono">{apt.uhid||''}</p>

                  {/* Time + type */}
                  <p className="text-xs text-slate-400 mt-1">
                    ⏰ {(apt.start_time||apt.startTime||'').slice(0,5)||'—'} · {apt.consultation_type||apt.consultationType||'OPD'}
                  </p>

                  {/* Calling status badge */}
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CALLING_STYLE[apt.callingStatus||'WAITING']}`}>
                      {CALLING_LABEL[apt.callingStatus||'WAITING']}
                    </span>

                    {/* Quick actions */}
                    {apt.callingStatus!=='DONE'&&apt.status!=='COMPLETED'&&(
                      <div className="flex gap-2" onClick={e=>e.stopPropagation()}>
                        {(!apt.callingStatus||apt.callingStatus==='WAITING')&&(
                          <button onClick={async()=>{await api.updateCallingStatus(apt.id,'CALLED');setQueue(q=>q.map((x:any)=>x.id===apt.id?{...x,callingStatus:'CALLED'}:x));}}
                            className="text-xs font-semibold text-[#02357A] hover:underline">
                            Call
                          </button>
                        )}
                        {apt.callingStatus==='CALLED'&&<>
                          <button onClick={async()=>{await api.updateCallingStatus(apt.id,'IN_CONSULTATION');setQueue(q=>q.map((x:any)=>x.id===apt.id?{...x,callingStatus:'IN_CONSULTATION'}:x));}}
                            className="text-xs font-semibold text-green-600 hover:underline">Arrived</button>
                          <button onClick={async()=>{await api.updateCallingStatus(apt.id,'SKIPPED');setQueue(q=>q.map((x:any)=>x.id===apt.id?{...x,callingStatus:'SKIPPED'}:x));}}
                            className="text-xs font-semibold text-red-500 hover:underline">Skip</button>
                        </>}
                        {apt.callingStatus==='SKIPPED'&&(
                          <button onClick={async()=>{await api.updateCallingStatus(apt.id,'WAITING');setQueue(q=>q.map((x:any)=>x.id===apt.id?{...x,callingStatus:'WAITING'}:x));}}
                            className="text-xs font-semibold text-[#02357A] hover:underline">Re-queue</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — Main panel */}
        <div className="lg:col-span-2 xl:col-span-3">

          {/* No patient selected */}
          {!selected&&(
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-20 text-center">
              <span className="text-6xl block mb-4">👆</span>
              <h3 className="text-lg font-bold text-slate-700 mb-2">Select a Patient</h3>
              <p className="text-slate-400 text-sm">Click on any patient in the queue to start consultation</p>
            </div>
          )}

          {selected&&(
            <>
              {/* Patient info bar */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#015CA6] to-[#039089] flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                  {(selected.patient_first_name||'P')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-slate-800 text-base">
                      {`${selected.patient_first_name||''} ${selected.patient_last_name||''}`.trim()||'—'}
                    </h3>
                    <span className="font-mono text-xs text-[#02357A] bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100">{selected.uhid||'—'}</span>
                    <span className="text-xs bg-[#02357A] text-white px-2 py-0.5 rounded-full font-bold">{selected.token_number||selected.tokenNumber}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">
                    👨‍⚕️ {docName(selected.doctor_first_name,selected.doctor_last_name)}
                    {selected.specialization&&<span className="text-slate-400"> · {selected.specialization}</span>}
                    <span className="mx-2 text-slate-300">·</span>
                    ⏰ {(selected.start_time||selected.startTime||'').slice(0,5)||'—'}
                    <span className="mx-2 text-slate-300">·</span>
                    {selected.consultation_type||selected.consultationType||'OPD'}
                  </p>
                </div>
                <button onClick={()=>navigate('/doctor/emr',{state:{patientId:selected.patient_id||selected.patientId}})}
                  className="flex-shrink-0 text-xs font-semibold text-[#02357A] bg-blue-50 hover:bg-blue-100 border border-blue-200 px-4 py-2 rounded-xl transition-all">
                  🩺 Full EMR
                </button>
                {(selected.patient_id||selected.patientId) && (
                  <div className="w-full mt-3">
                    <ShrPanel
                      patientId={selected.patient_id||selected.patientId}
                      encounterId={lastConsultId||selected.id}
                      visitType="OUTPATIENT"
                      compact
                    />
                  </div>
                )}
                {(shaStatus?.hasClaim || isShaCoveredPatient(selected)) && (
                <button
                  onClick={()=>{
                    const pid=selected.patient_id||selected.patientId;
                    const visitId=lastConsultId||selected.id;
                    const shaId=shaStatus?.claimId;
                    const cr=selected.sha_beneficiary_cr_id||selected.shaBeneficiaryCrId||'';
                    navigate(shaId
                      ? `/sha/claims/${shaId}?from=opd&visitId=${visitId||''}&patientId=${pid||''}&crId=${cr}&patientName=${encodeURIComponent(`${selected.patient_first_name||selected.first_name||''} ${selected.patient_last_name||selected.last_name||''}`.trim())}`
                      : `/sha/new-claim?from=opd&visitId=${visitId||''}&patientId=${pid||''}&crId=${cr}&patientName=${encodeURIComponent(`${selected.patient_first_name||selected.first_name||''} ${selected.patient_last_name||selected.last_name||''}`.trim())}&serviceType=OUTPATIENT`);
                  }}
                  className="flex-shrink-0 text-xs font-semibold text-white bg-[#039089] hover:bg-[#02357A] px-4 py-2 rounded-xl transition-all">
                  {shaStatus?.hasClaim ? `🛡️ SHA ${shaStatus.status||'Claim'}` : '+ SHA Claim'}
                </button>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                {TABS.map(([t,l])=>(
                  <Tab key={t} active={tab===t} onClick={()=>setTab(t)}>{l}</Tab>
                ))}
              </div>

              {/* ── QUEUE TAB ── */}
              {tab==='queue'&&(
                <Card title="Appointment Details" subtitle={`Token ${selected.token_number||selected.tokenNumber}`}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                    {[
                      ['Appointment #',selected.appointment_number||selected.appointmentNumber||'—'],
                      ['Time',(selected.start_time||selected.startTime||'').slice(0,5)||'—'],
                      ['Type',selected.consultation_type||selected.consultationType||'OPD'],
                      ['Status',selected.status||'—'],
                      ['Queue #',selected.queue_number||'—'],
                      ['Reason',selected.reason_for_visit||selected.reasonForVisit||'General Consultation'],
                    ].map(([l,v])=>(
                      <div key={l} className="bg-slate-50 rounded-xl p-3">
                        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide">{l}</p>
                        <p className="font-semibold text-slate-800 mt-1 text-sm">{v}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={()=>setTab('vitals')} className="py-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 font-semibold text-sm transition-all">❤️ Record Vitals</button>
                    <button onClick={()=>setTab('consult')} className="py-3 rounded-xl bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold text-sm shadow-lg shadow-[#02357A]/20 transition-all">📋 Start Consultation</button>
                  </div>
                </Card>
              )}

              {/* ── VITALS TAB ── */}
              {tab==='vitals'&&(
                <Card
                  title="Record Vitals"
                  subtitle={`Patient: ${selected.patient_first_name||''} ${selected.patient_last_name||''}`}
                  action={
                    <button onClick={()=>loadVitalsHistory(selected.patient_id||selected.patientId)}
                      className="text-xs font-semibold text-[#02357A] bg-blue-50 hover:bg-blue-100 border border-blue-100 px-3 py-1.5 rounded-lg transition-all">
                      📊 View History ({vitalsHistory.length})
                    </button>
                  }
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
                    {[['temperature','🌡️ Temp (°C)','36.8'],['systolicBp','💓 Systolic BP','120'],['diastolicBp','💓 Diastolic BP','80'],['heartRate','❤️ Heart Rate (bpm)','72'],['respiratoryRate','🫁 Resp Rate','16'],['oxygenSaturation','💨 SpO₂ (%)','98'],['weight','⚖️ Weight (kg)','70'],['height','📏 Height (cm)','170'],['bloodSugar','🩸 Blood Sugar (mg/dL)',''],['painScore','😣 Pain Score (0-10)','']].map(([k,l,ph])=>(
                      <div key={k}>
                        <Lbl>{l}</Lbl>
                        <input type="number" step="0.1" placeholder={ph as string} value={(vitals as any)[k]}
                          onChange={(e:any)=>setVitals({...vitals,[k]:e.target.value})} className={inputCls}/>
                      </div>
                    ))}
                    <div>
                      <Lbl>🧮 BMI (auto-calculated)</Lbl>
                      <div className={`input bg-slate-50 flex items-center font-bold text-sm
                        ${bmi!=='—'&&+bmi>=30?'text-red-600':bmi!=='—'&&+bmi>=25?'text-amber-600':bmi!=='—'?'text-green-600':'text-slate-400'}`}>
                        {bmi!=='—'?`${bmi} kg/m²`:'Enter weight & height'}
                      </div>
                    </div>
                  </div>
                  <button onClick={saveVitals} disabled={saving}
                    className="w-full py-3 bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold rounded-xl shadow-lg shadow-[#02357A]/20 transition-all disabled:opacity-50">
                    {saving?'Saving…':'✅ Save Vitals & Continue to Consultation'}
                  </button>

                  {/* Inline Vitals History */}
                  {vitalsHistory.length>0&&(
                    <div className="mt-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">📊 Previous Vitals</span>
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{vitalsHistory.length} records</span>
                      </div>
                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="w-full text-xs min-w-max">
                          <thead><tr className="bg-slate-50 border-b">{['Date','Temp','BP','HR','SpO₂','Wt','BMI','Sugar','Pain'].map(h=><th key={h} className="px-3 py-2 text-left text-slate-500 font-semibold">{h}</th>)}</tr></thead>
                          <tbody className="divide-y divide-slate-100">
                            {vitalsHistory.slice(0,5).map((v:any,i:number)=>(
                              <tr key={i} className="hover:bg-blue-50/30">
                                <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{v.recordedAt?new Date(v.recordedAt).toLocaleDateString('en-KE'):v.created_at?.slice(0,10)||'—'}</td>
                                <td className="px-3 py-2 font-medium">{v.temperature||'—'}°C</td>
                                <td className="px-3 py-2 font-semibold text-[#02357A]">{v.systolicBp||'—'}/{v.diastolicBp||'—'}</td>
                                <td className="px-3 py-2">{v.heartRate||'—'}</td>
                                <td className="px-3 py-2">{v.oxygenSaturation||v.spo2||'—'}%</td>
                                <td className="px-3 py-2">{v.weight||'—'}kg</td>
                                <td className="px-3 py-2">{v.bmi||'—'}</td>
                                <td className="px-3 py-2">{v.bloodSugar||'—'}</td>
                                <td className="px-3 py-2">{v.painScore!=null?`${v.painScore}/10`:'—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {vitalsHistory.length>5&&<button onClick={()=>setShowVitalsHistory(true)} className="text-xs text-[#02357A] hover:underline mt-2">View all {vitalsHistory.length} records →</button>}
                    </div>
                  )}
                </Card>
              )}

              {/* ── CONSULTATION TAB ── */}
              {tab==='consult'&&(
                <Card title="Clinical Notes" subtitle="Fill all relevant sections">
                  <div className="space-y-4">
                    {([['chiefComplaint','Chief Complaint *',3],['historyOfPresentIllness','History of Present Illness',3],['physicalExamination','Physical Examination',2],['pastMedicalHistory','Past Medical History',2],['provisionalDiagnosis','Provisional Diagnosis',2],['finalDiagnosis','Final Diagnosis',2]] as [string,string,number][]).map(([k,l,rows])=>(
                      <div key={k}>
                        <Lbl>{l}</Lbl>
                        <textarea rows={rows} className={textareaCls} value={(consult as any)[k]}
                          onChange={(e:any)=>setConsult({...consult,[k]:e.target.value})}/>
                      </div>
                    ))}
                    <div>
                      <TerminologySearch
                        label="ICD-11 Diagnosis Code"
                        system="icd11"
                        value={consult.icdCode}
                        onChange={(display:string,code?:string)=>{
                          // TerminologySearch: onChange(value, code, displayText)
                          // ICD11 returns: { code, description, type }
                          const icdCode=code||display;
                          const diagText=display;
                          setConsult(c=>({...c,
                            icdCode:icdCode,
                            finalDiagnosis:c.finalDiagnosis||diagText
                          }));
                        }}
                        placeholder="Search ICD-11 — e.g. CA07, Diabetes, Hypertension…"
                      />
                      {consult.icdCode&&(
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-100 px-2 py-1 rounded-lg font-mono font-bold">{consult.icdCode}</span>
                          <button onClick={()=>setConsult(c=>({...c,icdCode:''}))} className="text-xs text-slate-400 hover:text-red-500">✕ Clear</button>
                        </div>
                      )}
                    </div>
                    {([['treatmentPlan','Treatment Plan',3],['advice','Advice & Patient Instructions',2]] as [string,string,number][]).map(([k,l,rows])=>(
                      <div key={k}>
                        <Lbl>{l}</Lbl>
                        <textarea rows={rows} className={textareaCls} value={(consult as any)[k]}
                          onChange={(e:any)=>setConsult({...consult,[k]:e.target.value})}/>
                      </div>
                    ))}

                    {/* Follow-up */}
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <label className="flex items-center gap-2 cursor-pointer mb-3">
                        <input type="checkbox" checked={consult.followUpRequired}
                          onChange={e=>setConsult({...consult,followUpRequired:e.target.checked})}
                          className="w-4 h-4 accent-[#02357A]"/>
                        <span className="text-sm font-semibold text-slate-700">Schedule Follow-up Visit</span>
                      </label>
                      {consult.followUpRequired&&(
                        <div className="grid grid-cols-2 gap-3">
                          <div><Lbl>Follow-up Date</Lbl><input type="date" value={consult.followUpDate} onChange={e=>setConsult({...consult,followUpDate:e.target.value})} className={inputCls}/></div>
                          <div><Lbl>{t('common.reason','Reason')}</Lbl><input value={consult.followUpReason} onChange={e=>setConsult({...consult,followUpReason:e.target.value})} placeholder="Review results, check progress…" className={inputCls}/></div>
                        </div>
                      )}
                    </div>

                    <button onClick={saveConsultation} disabled={saving}
                      className="w-full py-3 bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold rounded-xl shadow-lg transition-all disabled:opacity-50">
                      {saving?'Saving…':'💾 Save & Continue to Prescription'}
                    </button>

                    {/* Consultation History */}
                    {consultationHistory.length>0&&(
                      <div className="mt-5">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">📋 Previous Consultations ({consultationHistory.length})</p>
                        <div className="space-y-2">
                          {consultationHistory.slice(0,3).map((c:any,i:number)=>(
                            <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-xs text-slate-400">{(c.consultation_date||c.created_at)?new Date(c.consultation_date||c.created_at).toLocaleDateString('en-KE',{day:'2-digit',month:'short',year:'numeric'}):'—'}</span>
                                <div className="flex gap-1.5">
                                  {(c.icd_code||c.icdCode)&&<span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-mono font-bold">{c.icd_code||c.icdCode}</span>}
                                  <span className="text-xs bg-blue-50 text-[#02357A] px-2 py-0.5 rounded-full">OPD</span>
                                </div>
                              </div>
                              {(c.chief_complaint||c.chiefComplaint)&&<p className="text-xs font-semibold text-slate-700">🗣️ {c.chief_complaint||c.chiefComplaint}</p>}
                              {(c.final_diagnosis||c.finalDiagnosis)&&<p className="text-xs text-slate-800 mt-1">🔬 <strong>{c.final_diagnosis||c.finalDiagnosis}</strong></p>}
                              {(c.provisional_diagnosis||c.provisionalDiagnosis)&&!(c.final_diagnosis||c.finalDiagnosis)&&<p className="text-xs text-slate-600 mt-1">🔬 {c.provisional_diagnosis||c.provisionalDiagnosis} (provisional)</p>}
                              {(c.treatment_plan||c.treatmentPlan)&&<p className="text-xs text-slate-500 mt-1 line-clamp-2">💊 {c.treatment_plan||c.treatmentPlan}</p>}
                              {(c.advice||c.advice)&&<p className="text-xs text-slate-400 mt-1 line-clamp-1">📋 {c.advice}</p>}
                            </div>
                          ))}
                          {consultationHistory.length>3&&<p className="text-xs text-slate-400 text-center">+{consultationHistory.length-3} more consultations</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* ── PROCEDURES TAB ── */}
              {tab==='proc'&&(
                <Card title="Procedures" subtitle="Log OPD procedures for billing">
                  {procCatalog.length>0&&(
                    <div className="mb-5">
                      <Lbl>Quick Add — Common Procedures</Lbl>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                        {procCatalog.map((p:any)=>(
                          <button key={p.id} onClick={()=>logProcedure(p)} disabled={saving}
                            className="text-left p-3 rounded-xl border-2 border-slate-200 hover:border-[#02357A] hover:bg-blue-50 transition-all group">
                            <p className="text-xs font-semibold text-slate-700 group-hover:text-[#02357A]">{p.procedureName}</p>
                            <p className="text-sm font-bold text-green-600 mt-1">{getCurrencySymbol()}{p.cost||'—'}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-5">
                    <Lbl>Custom Procedure</Lbl>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <div className="col-span-2"><input placeholder="Procedure name" value={procForm.procedureName} onChange={e=>setProcForm({...procForm,procedureName:e.target.value})} className={inputCls}/></div>
                      <input type="number" placeholder="Cost ({getCurrencySymbol()})" value={procForm.cost} onChange={e=>setProcForm({...procForm,cost:e.target.value})} className={inputCls}/>
                      <div className="col-span-3"><input placeholder="Notes (optional)" value={procForm.notes} onChange={e=>setProcForm({...procForm,notes:e.target.value})} className={inputCls}/></div>
                      <div className="col-span-3">
                        <button onClick={()=>logProcedure()} disabled={saving}
                          className="w-full py-2.5 bg-[#039089] hover:bg-teal-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50">
                          {saving?'Logging…':'+ Log Procedure'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {patientProcedures.length>0&&(
                    <div>
                      <Lbl>Logged Today</Lbl>
                      <div className="space-y-2 mt-1">
                        {patientProcedures.map((p:any)=>(
                          <div key={p.id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                            <div>
                              <p className="font-semibold text-sm text-slate-800">{p.procedureName}</p>
                              <p className="text-xs text-slate-400">{fmtDateTime(p.performedAt)}{p.notes?` · ${p.notes}`:''}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{getCurrencySymbol()}{p.cost||'—'}</p>
                              <p className={`text-xs ${p.billed?'text-green-500':'text-amber-500'}`}>{p.billed?'Billed':'Pending'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* ── PRESCRIPTION TAB ── */}
              {tab==='rx'&&(
                <Card title="Prescription" subtitle="Add drugs and dosage instructions"
                  action={<button onClick={addRxItem} className="text-xs font-semibold text-[#02357A] bg-blue-50 hover:bg-blue-100 border border-blue-100 px-3 py-1.5 rounded-lg">+ Add Drug</button>}>
                  <div className="space-y-3 mb-5">
                    {rxItems.map((item,i)=>(
                      <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                          <div className="col-span-2 md:col-span-1">
                          <Lbl>Drug Name *</Lbl>
                          <DrugSearchInput
                            value={item.drugName}
                            onChange={(name,drug)=>{
                              updateRxItem(i,'drugName',name);
                              if(drug){
                                if(drug.strength)updateRxItem(i,'dosage',drug.strength);
                                if(drug.dosageForm){
                                  const routeMap:any={TABLET:'Oral',CAPSULE:'Oral',SYRUP:'Oral',INJECTION:'IV',CREAM:'Topical',OINTMENT:'Topical',DROPS:'Topical',INHALER:'Inhaled'};
                                  const r=routeMap[drug.dosageForm.toUpperCase()];
                                  if(r)updateRxItem(i,'route',r);
                                }
                              }
                            }}
                            placeholder="Search drug from pharmacy…"
                          />
                        </div>
                          <div><Lbl>Strength / Dosage</Lbl><input value={item.dosage} onChange={e=>updateRxItem(i,'dosage',e.target.value)} placeholder="e.g. 500mg" className={inputCls}/></div>
                          <div><Lbl>Frequency</Lbl><select value={item.frequency} onChange={e=>updateRxItem(i,'frequency',e.target.value)} className={selectCls}>{DRUG_FREQ.map(f=><option key={f}>{f}</option>)}</select></div>
                          <div><Lbl>Route</Lbl><select value={item.route} onChange={e=>updateRxItem(i,'route',e.target.value)} className={selectCls}>{MASTER_ROUTES.map(r=><option key={r}>{r}</option>)}</select></div>
                          <div><Lbl>Duration</Lbl><input value={item.duration} onChange={e=>updateRxItem(i,'duration',e.target.value)} placeholder="e.g. 5 days" className={inputCls}/></div>
                          <div><Lbl>Instructions</Lbl><input value={item.instructions} onChange={e=>updateRxItem(i,'instructions',e.target.value)} placeholder="After food" className={inputCls}/></div>
                        </div>
                        {rxItems.length>1&&(
                          <button onClick={()=>removeRxItem(i)} className="text-xs text-red-500 hover:text-red-700 font-medium">✕ Remove drug</button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <button onClick={savePrescription} disabled={saving}
                      className="py-3 bg-[#02357A] hover:bg-[#015CA6] text-white font-semibold rounded-xl shadow-lg shadow-[#02357A]/20 transition-all disabled:opacity-50">
                      {saving?'Saving…':'💾 Save Prescription'}
                    </button>
                    <button onClick={printPrescription}
                      className="py-3 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-xl border border-slate-200 transition-all">
                      🖨️ Print Prescription
                    </button>
                  </div>
                  <button onClick={closeVisitAndPrint} disabled={saving}
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/20 transition-all disabled:opacity-50">
                    ✅ Close Visit & Print Summary
                  </button>

                  {/* Prescription History */}
                  {prescriptionHistory.length>0&&(
                    <div className="mt-5">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">💊 Previous Prescriptions ({prescriptionHistory.length})</p>
                      <div className="space-y-2">
                        {prescriptionHistory.slice(0,3).map((rx:any,i:number)=>(
                          <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-mono text-xs text-[#02357A] font-bold">{rx.prescriptionNumber||rx.prescription_number||`RX-${i+1}`}</span>
                              <span className="text-xs text-slate-400">{rx.prescriptionDate?.slice(0,10)||rx.created_at?.slice(0,10)||'—'}</span>
                            </div>
                            {(rx.items||rx.prescription_items||[]).slice(0,3).map((item:any,j:number)=>(
                              <div key={j} className="flex items-center gap-2 text-xs text-slate-600 mt-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#02357A] flex-shrink-0"/>
                                <span className="font-medium">{item.drugName||item.drug_name}</span>
                                <span className="text-slate-400">{item.dosage||''} · {item.frequency||''} · {item.duration||''}</span>
                              </div>
                            ))}
                            {(rx.items||rx.prescription_items||[]).length>3&&<p className="text-xs text-slate-400 mt-1">+{(rx.items||rx.prescription_items||[]).length-3} more drugs</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Vitals History Slide Panel */}
      <SlidePanel isOpen={showVitalsHistory} onClose={()=>setShowVitalsHistory(false)} title="Vitals History" size="lg">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{['Date & Time','Temp','BP (mmHg)','Heart Rate','SpO₂','Weight','BMI','Sugar','Pain'].map(h=>(
                <th key={h} className="px-3 py-2.5 text-left text-xs text-slate-500 font-bold uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vitalsHistory.map((v:any,i)=>(
                <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                  <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDateTime(v.recordedAt)}</td>
                  <td className="px-3 py-2.5 font-medium">{v.temperature||'—'}°C</td>
                  <td className="px-3 py-2.5 font-semibold text-[#02357A]">{v.systolicBp||'—'}/{v.diastolicBp||'—'}</td>
                  <td className="px-3 py-2.5">{v.heartRate||'—'} bpm</td>
                  <td className="px-3 py-2.5">{v.oxygenSaturation||v.spo2||'—'}%</td>
                  <td className="px-3 py-2.5">{v.weight||'—'} kg</td>
                  <td className="px-3 py-2.5">{v.bmi||'—'}</td>
                  <td className="px-3 py-2.5">{v.bloodSugar||'—'}</td>
                  <td className="px-3 py-2.5">{v.painScore!=null?`${v.painScore}/10`:'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {vitalsHistory.length===0&&(
            <div className="py-12 text-center text-slate-400">
              <p className="text-3xl mb-2">❤️</p>
              <p className="text-sm">No vitals recorded yet</p>
            </div>
          )}
        </div>
      </SlidePanel>
    </Layout>
  );

  function addRxItem(){setRxItems(p=>[...p,{drugName:'',dosage:'',frequency:'Once daily',route:'Oral',duration:'5 days',instructions:'After food'}]);}
  function removeRxItem(i:number){setRxItems(p=>p.filter((_,idx)=>idx!==i));}
  function updateRxItem(i:number,k:string,v:string){setRxItems(p=>p.map((item,idx)=>idx===i?{...item,[k]:v}:item));}
}
